<script lang="ts">
	import { Background } from "@components/Background.js";
	import BookMeeting from "@components/BookMeeting.svelte";
	import FaqItems from "@components/FaqItems.svelte";
	import FoldingCards from "@components/FoldingCards.svelte";
	import HighlightedProjects from "@components/HighlightedProjects.svelte";
	import InfoCard from "@components/InfoCard.svelte";
	import Ol from "@components/Ol.svelte";
	import { type OlProps, OlSize } from "@components/OlProps.js";
	import Pill from "@components/Pill.svelte";
	import { PillColor, PillSize } from "@components/PillProps.js";
	import Section from "@components/Section.svelte";
	import TechnologyCards from "@components/TechnologyCards.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import arrowNext from "@core/assets/icons/arrow-next.svg?raw";
	import brackets from "@core/assets/icons/brackets.svg?raw";
	import { getConnectWithFastestPath } from "@core/schema/paths/getConnectWithFastestPath.js";
	import { getCrmPath } from "@core/schema/paths/getCrmPath.js";
	import { getDevelopmentPath } from "@core/schema/paths/getDevelopmentPath.js";
	import { getIntegrationsPath } from "@core/schema/paths/getIntegrationsPath.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { getBenefitsWebApps } from "../data/getBenefitsWebApps.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import { getFaqsWebApps } from "../data/getFaqsWebApps.js";
	import { getIdealForEverybodyWebApps } from "../data/getIdealForEverybodyWebApps.js";
	import { getReasonsForCoop } from "../data/getReasonsForCoop.js";
	import { getTechnologies } from "../data/getTechnologies.js";
	import type { WebAppsProps } from "./WebAppsProps.js";

	const _: WebAppsProps = $props();
	const { tenant } = getGlobalTemplateProps();
	const webAppFunctions = [
		"Správa uživatelů",
		"Zákaznické účty",
		"Platební brána",
		"CRM integrace",
		"Analytické nástroje",
		"Notifikace",
		"Vyhledávání s filtry",
		"Chatovací okno",
	];
	const devProcess: OlProps[] = [
		{ title: "Úvodní analýza" },
		{ title: "Návrh a prototyp" },
		{ title: "Vývoj a implementace" },
		{ title: "Testování a spuštění" },
		{ title: "Podpora a údržba" },
	];
</script>

<Layout withStructuredData>
	<Section background={Background.LightGradient} centered gridHeadline="container-6" gridDesc="container-6" hero>
		{#snippet headline()}
			<Formatted text="Vytvoříme vám cg[[webovou aplikaci]] na míru – od myšlenky po realizaci" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Neomezujte se krabicovým řešením. Vyvineme webovou aplikaci přesně tak, sp[[jak si přejete.]]"
				/>
			</p>
		{/snippet}
		{#snippet cta()}
			<Link
				variant={ButtonVariant.PhloxOutlineLightBg}
				class="flex items-center justify-center mx-auto mt-2"
				href={getConnectWithFastestPath(tenant)}
			>
				<Formatted text="Rezervovat online konzultaci" />
				<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
			</Link>
		{/snippet}
	</Section>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
		paddingFollowNext
	>
		{#snippet headline()}
			<Formatted text="cg[[Vývoj]] webových aplikací pro cg[[potřeby]] vaší firmy" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="[{getDevelopmentPath(
						tenant,
					)}][[Vývoj na míru]] děláme proto, abychom webovou aplikaci mohli dokonale přizpůsobit potřebám vašeho podnikání. Proč se spoléhat na předpřipravená krabicová řešení, která často svazují ruce?"
				/>
			</p>
			<p>
				<Formatted
					text="Webovou aplikaci (ale také mobilní) dokážeme [{getIntegrationsPath(
						tenant,
					)}][[integrovat]] s vaším [{getCrmPath(
						tenant,
					)}][[CRM]], SCM, ERP, platební bránou a řadou dalších systémů. sp[[Většinou se vydáváme cestou tzv. agilního vývoje.]]"
				/>
			</p>
			<p>
				<Formatted text="Klademe důraz mimo jiné na sp[[rychlost aplikací, vyladěné UX/UI a technické SEO.]]" />
			</p>
		{/snippet}
	</Section>

	<Section
		background={Background.LightGradient}
		noPadding="bot"
		gridHeadline="container-6 lg:text-center"
		gridDesc="container-8"
		centered
	>
		{#snippet headline()}
			<Formatted text="cg[[Výhody]] webových aplikací" />
		{/snippet}
	</Section>
	<Section gridChildren="max-md:full-width" background={Background.White} noPadding="both">
		<div
			class="grid grid-cols-1 md:grid-cols-3 gap-1 max-lg:full-width lg:px-1 bg-fstlight-100 border-t-4 border-fstlight-100"
		>
			{#each getBenefitsWebApps(tenant) as { title }, index (index)}
				<InfoCard {title} indexNumber={index + 1} />
			{/each}
		</div>
	</Section>
	<Link
		variant={ButtonVariant.Phlox}
		class="flex items-center justify-center mx-auto mt-8 lg:mt-12"
		href={getConnectWithFastestPath(tenant)}
	>
		<Formatted text="Chci webovou aplikaci" />
		<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
	</Link>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
	>
		{#snippet headline()}
			<Formatted text="cg[[Ideální řešení]] pro e-shopy i startupy" />
			<span class="block text-2md font-bold mt-6">
				<Formatted
					text="Pomocí webových aplikací cg[[pomáháme]] růst firmám z e-commerce i technologickým startupům."
				/>
			</span>
		{/snippet}
		{#snippet desc()}
			<div class="flex flex-col gap-8">
				<Ol items={getIdealForEverybodyWebApps(tenant)} size={OlSize.Medium} />
			</div>
		{/snippet}
		<Link
			variant={ButtonVariant.Phlox}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getConnectWithFastestPath(tenant)}
			><Formatted text="Chci nezávaznou konzultaci" />
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
		paddingFollowNext
	>
		{#snippet headline()}
			<Formatted text="cg[[Jaké funkce]] můžeme do webové aplikace zahrnout" />
		{/snippet}
		{#snippet desc()}
			<div class="flex flex-wrap gap-1 lg:gap-2">
				{#each webAppFunctions as fnct (fnct)}
					<Pill size={PillSize.Medium} color={PillColor.White}>
						<Icon icon={brackets} square class="text-fstgreen h-5"></Icon>
						<span>{fnct}</span>
					</Pill>
				{/each}
			</div>
		{/snippet}
	</Section>

	<Section centered gridHeadline="container-8" noPadding="bot">
		{#snippet headline()}
			<Formatted text="cg[[Webová aplikace]] jako součást zakázky pro cukrárnu i Volvo" />
		{/snippet}
	</Section>
	<HighlightedProjects />

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
	>
		{#snippet headline()}
			<Formatted text="cg[[Proces]] vývoje webových aplikací" />
		{/snippet}
		{#snippet desc()}
			<div class="flex flex-col gap-8">
				<Ol items={devProcess} size={OlSize.Medium} />
			</div>
		{/snippet}
	</Section>

	<Section gridHeadline="container-6" background={Background.LightGradient} noPadding="bot" centered>
		{#snippet headline()}
			<Formatted text="Spoléháme na cg[[moderní]] a cg[[efektivní]] technologie" />
		{/snippet}
	</Section>
	<TechnologyCards technologies={getTechnologies(tenant)} />
	<Link
		variant={ButtonVariant.Phlox}
		class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
		href={getConnectWithFastestPath(tenant)}
		>Chci fastest služby
		<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
	</Link>

	<Section centered gridHeadline="container-6">
		{#snippet headline()}
			<Formatted text="Často kladené otázky k cg[[vývoji webových aplikací]]" />
		{/snippet}
		<FaqItems faqs={getFaqsWebApps(tenant)} />
	</Section>

	<Section
		background={Background.Onyx}
		gridHeadline="container-6 lg:container-start-1 lg:container-end-5 xl:container-start-2 xl:container-end-4 max-lg:text-center"
		gridDesc="lg:container-start-7 lg:container-end-12 xl:container-start-6 xl:container-end-11"
	>
		{#snippet headline()}
			<Formatted text="cg[[Proč]] si plácnout s cg[[Fastest Solution]]" />
		{/snippet}
		{#snippet desc()}
			<FoldingCards color={Background.Onyx} cards={getReasonsForCoop(tenant)} />
		{/snippet}
		<div class="container-12">
			<Link
				variant={ButtonVariant.Phlox}
				class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
				href={getConnectWithFastestPath(tenant)}
				>Chci fastest služby
				<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
			</Link>
		</div>
	</Section>

	{@const contacts = getBookMeetingPeople(tenant)}
	<BookMeeting contacts={[contacts[BookMeetingPerson.Kucera], contacts[BookMeetingPerson.Svejda]]} />
</Layout>
